/** Поля ввода с счётчиками, тулбаром разметки, загрузкой файлов и выбором цвета. */
import { uid } from '../../../src/shared/factory.js';
import { len } from '../../../src/shared/validate.js';
import { DEMO, api, demoUploads, previewUrl } from '../api.js';
import { h, hexColor, icon, parseHex } from '../dom.js';
import { popover, toast } from './overlay.js';
import { emojiPicker, insertText, mentionPicker, prefixLines, timestampPicker } from './pickers.js';

export type Toolbar = 'full' | 'inline' | 'none';

export interface TextOpts {
  label?: string;
  value: string;
  onInput: (v: string) => void;
  max?: number;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  toolbar?: Toolbar;
  hint?: string;
  mono?: boolean;
  /** дополнительный элемент справа от поля (например, кнопка) */
  addon?: HTMLElement;
  required?: boolean;
}

function counter(max: number | undefined) {
  const el = h('span', { class: 'counter' });
  const update = (v: string) => {
    if (!max) return;
    const n = len(v);
    el.textContent = `${n}/${max}`;
    el.classList.toggle('warn', n > max * 0.9 && n <= max);
    el.classList.toggle('over', n > max);
  };
  return { el, update };
}

function autoGrow(t: HTMLTextAreaElement) {
  t.style.height = 'auto';
  t.style.height = `${Math.min(t.scrollHeight + 2, 520)}px`;
}

function toolbarFor(kind: Toolbar, getEl: () => HTMLInputElement | HTMLTextAreaElement): HTMLElement | null {
  if (kind === 'none') return null;
  const b = (ic: string, title: string, fn: (btn: HTMLElement) => void) => {
    const btn: HTMLButtonElement = h(
      'button',
      {
        class: 'tb-btn',
        type: 'button',
        title,
        attrs: { 'aria-label': title },
        onmousedown: (e: MouseEvent) => e.preventDefault(),
        onclick: () => fn(btn),
      },
      icon(ic, 15),
    );
    return btn;
  };
  const wrap = (a: string, z = a, ph = 'текст') => () => insertText(getEl(), a, z, ph);
  const items: HTMLElement[] = [
    b('bold', 'Жирный (Ctrl+B)', wrap('**')),
    b('italic', 'Курсив (Ctrl+I)', wrap('*')),
    b('underline', 'Подчёркнутый (Ctrl+U)', wrap('__')),
    b('strike', 'Зачёркнутый', wrap('~~')),
    b('spoiler', 'Спойлер', wrap('||')),
    b('code', 'Код', wrap('`', '`', 'код')),
  ];
  if (kind === 'full') {
    const area = () => getEl();
    items.push(
      h('span', { class: 'tb-sep' }),
      b('heading', 'Заголовок (повторно - меньше)', () => {
        const el = area();
        const v = el.value;
        const start = el.selectionStart ?? 0;
        const lineStart = v.lastIndexOf('\n', start - 1) + 1;
        const line = v.slice(lineStart).split('\n')[0];
        const m = /^(#{1,3}) /.exec(line);
        const next = !m ? '# ' : m[1].length < 3 ? `${m[1]}# ` : '';
        el.setRangeText(next + line.replace(/^#{1,3} /, ''), lineStart, lineStart + line.length, 'end');
        el.dispatchEvent(new Event('input', { bubbles: true }));
      }),
      b('minus', 'Мелкий текст (-#)', () => prefixLines(area(), '-# ')),
      b('quote', 'Цитата', () => prefixLines(area(), '> ')),
      b('list', 'Список', () => prefixLines(area(), '- ')),
      b('code', 'Блок кода', () => insertText(area(), '```\n', '\n```', 'код')),
      b('link', 'Ссылка', () => {
        const el = area();
        const sel = el.value.slice(el.selectionStart ?? 0, el.selectionEnd ?? 0);
        insertText(el, '[', '](https://)', sel ? '' : 'текст');
      }),
      h('span', { class: 'tb-sep' }),
      b('at', 'Упоминание', (btn) => mentionPicker(btn, (v) => insertText(area(), v))),
    );
  }
  items.push(
    b('smile', 'Эмодзи', (btn) => emojiPicker(btn, (v) => insertText(getEl(), v))),
    b('clock', 'Время', (btn) => timestampPicker(btn, (v) => insertText(getEl(), v))),
  );
  return h('div', { class: 'toolbar' }, items);
}

export function textField(o: TextOpts): HTMLElement {
  const c = counter(o.max);
  let control: HTMLInputElement | HTMLTextAreaElement;
  if (o.multiline) {
    const t = h('textarea', { class: ['input', 'textarea', o.mono && 'mono'], value: o.value, placeholder: o.placeholder ?? '', rows: o.rows ?? 3, spellcheck: true });
    requestAnimationFrame(() => autoGrow(t));
    t.addEventListener('input', () => autoGrow(t));
    control = t;
  } else {
    control = h('input', { class: ['input', o.mono && 'mono'], value: o.value, placeholder: o.placeholder ?? '', spellcheck: false });
  }
  if (o.label) control.setAttribute('aria-label', o.label);
  control.addEventListener('input', () => {
    c.update(control.value);
    o.onInput(control.value);
  });
  control.addEventListener('keydown', (e: Event) => {
    const ev = e as KeyboardEvent;
    if (!(ev.ctrlKey || ev.metaKey) || o.toolbar === 'none' || !o.toolbar) return;
    const map: Record<string, string> = { b: '**', i: '*', u: '__' };
    const k = ev.key.toLowerCase();
    if (map[k]) {
      ev.preventDefault();
      insertText(control, map[k], map[k], 'текст');
    }
  });
  c.update(o.value);
  const tb = toolbarFor(o.toolbar ?? 'none', () => control);
  return h(
    'div',
    { class: ['field', o.multiline && 'is-multiline'] },
    o.label || o.max ? h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, o.label ?? '', o.required ? h('span', { class: 'req' }, '*') : null), c.el) : null,
    tb,
    o.addon ? h('div', { class: 'input-group' }, control, o.addon) : control,
    o.hint ? h('div', { class: 'field-hint' }, o.hint) : null,
  );
}

export function switchField(label: string, checked: boolean, onChange: (v: boolean) => void, hint?: string): HTMLElement {
  const input = h('input', { type: 'checkbox', checked, class: 'switch-input' });
  input.addEventListener('change', () => onChange(input.checked));
  return h(
    'label',
    { class: 'switch' },
    input,
    h('span', { class: 'switch-track' }, h('span', { class: 'switch-thumb' })),
    h('span', { class: 'switch-text' }, h('span', null, label), hint ? h('span', { class: 'switch-hint' }, hint) : null),
  );
}

export function selectField<T extends string>(label: string, value: T, options: { value: T; label: string }[], onChange: (v: T) => void): HTMLElement {
  const sel = h('select', { class: 'input select', attrs: { 'aria-label': label } }, options.map((o) => h('option', { value: o.value, selected: o.value === value }, o.label)));
  sel.addEventListener('change', () => onChange(sel.value as T));
  return h('label', { class: 'field' }, label ? h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, label)) : null, sel);
}

export function numberField(label: string, value: number, min: number, max: number, onChange: (v: number) => void, suffix?: string): HTMLElement {
  const input = h('input', { class: 'input', type: 'number', value: String(value), min: String(min), max: String(max), attrs: { 'aria-label': label } });
  input.addEventListener('input', () => {
    const n = Number(input.value);
    if (!Number.isNaN(n)) onChange(Math.round(n));
  });
  return h('label', { class: 'field' }, h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, label)), suffix ? h('div', { class: 'input-group' }, input, h('span', { class: 'input-suffix' }, suffix)) : input);
}

/* ---------- загрузка файлов ---------- */

export async function uploadFile(file: File): Promise<string> {
  if (DEMO) {
    const id = uid(16);
    demoUploads.set(id, URL.createObjectURL(file));
    return `upload://${id}/${encodeURIComponent(file.name)}`;
  }
  const res = await api.upload(file);
  return res.url;
}

export function pickFile(accept: string, multiple = false): Promise<File[]> {
  return new Promise((resolve) => {
    const input = h('input', { type: 'file', accept, multiple, style: { display: 'none' } });
    input.addEventListener('change', () => {
      resolve([...(input.files ?? [])]);
      input.remove();
    });
    document.body.append(input);
    input.click();
  });
}

/** Поле для картинки: ссылка, загрузка с компьютера, перетаскивание, вставка из буфера. */
export function mediaField(label: string, value: string, onChange: (v: string) => void, opts: { hint?: string; accept?: string } = {}): HTMLElement {
  const input = h('input', { class: 'input', value, placeholder: 'https://... или загрузите файл', spellcheck: false, attrs: { 'aria-label': label } });
  const thumb = h('div', { class: 'media-thumb' });
  const renderThumb = () => {
    const src = previewUrl(input.value.trim());
    thumb.replaceChildren(src && !input.value.startsWith('attachment://') ? h('img', { src, alt: '', referrerPolicy: 'no-referrer' }) : icon('image', 16));
  };
  const set = (v: string) => {
    input.value = v;
    renderThumb();
    onChange(v);
  };
  input.addEventListener('input', () => {
    renderThumb();
    onChange(input.value);
  });
  const doUpload = async (file: File) => {
    try {
      wrap.classList.add('is-loading');
      set(await uploadFile(file));
    } catch (err: any) {
      toast(err.message ?? 'Не удалось загрузить файл', 'error');
    } finally {
      wrap.classList.remove('is-loading');
    }
  };
  const upBtn = h(
    'button',
    {
      class: 'icon-btn',
      type: 'button',
      title: 'Загрузить с компьютера',
      onclick: async () => {
        const [f] = await pickFile(opts.accept ?? 'image/*');
        if (f) doUpload(f);
      },
    },
    icon('upload', 16),
  );
  input.addEventListener('paste', (e: ClipboardEvent) => {
    const file = [...(e.clipboardData?.files ?? [])][0];
    if (file) {
      e.preventDefault();
      doUpload(file);
    }
  });
  const wrap = h('div', { class: 'field media-field' }, h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, label)), h('div', { class: 'input-group' }, thumb, input, upBtn), opts.hint ? h('div', { class: 'field-hint' }, opts.hint) : null);
  wrap.addEventListener('dragover', (e) => {
    e.preventDefault();
    wrap.classList.add('is-drop');
  });
  wrap.addEventListener('dragleave', () => wrap.classList.remove('is-drop'));
  wrap.addEventListener('drop', (e) => {
    e.preventDefault();
    wrap.classList.remove('is-drop');
    const file = e.dataTransfer?.files?.[0];
    if (file) doUpload(file);
  });
  renderThumb();
  return wrap;
}

/* ---------- цвет ---------- */

export const COLOR_PRESETS = [
  0x7c5cff, 0x5865f2, 0x00b0f4, 0x1abc9c, 0x57f287, 0x3ba55d, 0xfee75c, 0xf0b232, 0xe67e22, 0xed4245, 0xeb459e, 0x9b59b6, 0x95a5a6, 0x2b2d31, 0xffffff,
];

export function colorField(label: string, value: number | null, onChange: (v: number | null) => void): HTMLElement {
  let current = value;
  const swatch = h('span', { class: 'swatch' });
  const text = h('span', { class: 'color-text' });
  const render = () => {
    swatch.style.background = current === null ? 'transparent' : hexColor(current);
    swatch.classList.toggle('is-none', current === null);
    text.textContent = current === null ? 'Без цвета' : hexColor(current).toUpperCase();
  };
  const set = (v: number | null) => {
    current = v;
    render();
    onChange(v);
  };
  const btn = h(
    'button',
    {
      class: 'input color-btn',
      type: 'button',
      attrs: { 'aria-label': label },
      onclick: () => {
        const native = h('input', { type: 'color', class: 'color-native', value: hexColor(current ?? 0x7c5cff) });
        const hex = h('input', { class: 'input input-sm mono', value: current === null ? '' : hexColor(current), placeholder: '#7C5CFF', maxLength: 7 });
        native.addEventListener('input', () => {
          hex.value = native.value;
          set(parseHex(native.value));
        });
        hex.addEventListener('input', () => {
          const v = parseHex(hex.value);
          if (v !== null) {
            native.value = hexColor(v);
            set(v);
          }
        });
        p = popover(
          btn,
          h(
            'div',
            { class: 'picker color-picker' },
            h('div', { class: 'color-row' }, native, hex),
            h(
              'div',
              { class: 'swatches' },
              COLOR_PRESETS.map((c) =>
                h('button', {
                  class: 'swatch-btn',
                  type: 'button',
                  title: hexColor(c),
                  style: { background: hexColor(c) },
                  onclick: () => {
                    native.value = hexColor(c);
                    hex.value = hexColor(c);
                    set(c);
                  },
                }),
              ),
            ),
            h('button', { class: 'btn btn-ghost btn-sm', type: 'button', onclick: () => { set(null); p?.close(); } }, icon('x', 14), 'Без цвета'),
          ),
          { width: 260 },
        );
      },
    },
    swatch,
    text,
  );
  let p: { close: () => void } | null = null;
  render();
  return h('div', { class: 'field' }, h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, label)), btn);
}

/** Поле эмодзи с выбором из панели. */
export function emojiField(label: string, value: string, onChange: (v: string) => void): HTMLElement {
  const input = h('input', { class: 'input', value, placeholder: '😀 или <:name:id>', spellcheck: false, attrs: { 'aria-label': label } });
  input.addEventListener('input', () => onChange(input.value));
  const btn = h(
    'button',
    {
      class: 'icon-btn',
      type: 'button',
      title: 'Выбрать эмодзи',
      onclick: () =>
        emojiPicker(btn, (v) => {
          input.value = v;
          onChange(v);
        }),
    },
    icon('smile', 16),
  );
  return h('div', { class: 'field' }, h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, label)), h('div', { class: 'input-group' }, input, btn));
}
